/**
 * Centralized fetch wrapper.
 *
 * Responsibilities:
 * - Prefix requests with `env.API_BASE` + `/api/v1`
 * - Attach Authorization header from STORAGE_KEYS.JWT_TOKEN
 * - Parse JSON bodies
 * - Normalize errors into ApiError
 * - Emit a CustomEvent('app:unauthorized') on 401 instead of hard-redirecting
 * - Provide a tiny hook for components/router to listen and navigate
 *
 * All code MUST go through this module — do not call `fetch` directly.
 */

import { STORAGE_KEYS } from '@/constants/storage-keys';
import { env } from '@/env';

import { ApiError, fromResponse } from './errors';

const API_PREFIX = '/api/v1';

export const UNAUTHORIZED_EVENT = 'app:unauthorized';

type RequestOptions = Omit<RequestInit, 'body'> & {
  /** Serialized as JSON body. */
  json?: unknown;
  /** Sent as-is (for FormData/Blob/etc.). */
  body?: BodyInit;
  /** Skip attaching the Authorization header (for /login, public endpoints). */
  skipAuth?: boolean;
};

function buildUrl(path: string): string {
  // path should start with '/'
  const normalized = path.startsWith('/') ? path : `/${path}`;
  // If caller already included '/api/v1', don't double-prefix.
  if (normalized.startsWith(API_PREFIX)) {
    return `${env.API_BASE}${normalized}`;
  }
  return `${env.API_BASE}${API_PREFIX}${normalized}`;
}

function getAuthHeader(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const token = window.localStorage.getItem(STORAGE_KEYS.JWT_TOKEN);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function dispatchUnauthorized(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
}

/**
 * Refresh-token interceptor (dossie 1.5).
 *
 * When a 401 lands *and* we have a refresh token on hand, swap it for a fresh
 * access token and retry the original request once. If the backend hasn't
 * shipped `/auth/refresh` yet, this silently no-ops — legacy behaviour stands.
 *
 * Shape expected on success: `{ token: string, refresh_token?: string }`.
 * A single in-flight refresh is deduplicated across concurrent 401s.
 */
let refreshPromise: Promise<string | null> | null = null;

async function tryRefreshAccessToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null;
  const refreshToken = window.localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  if (!refreshToken) return null;

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(buildUrl('/auth/refresh'), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (!res.ok) return null;
        const data = (await res.json()) as { token?: string; refresh_token?: string };
        if (!data.token) return null;
        window.localStorage.setItem(STORAGE_KEYS.JWT_TOKEN, data.token);
        if (data.refresh_token) {
          window.localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token);
        }
        return data.token;
      } catch {
        return null;
      } finally {
        // Allow next 401 to start a fresh attempt.
        setTimeout(() => {
          refreshPromise = null;
        }, 0);
      }
    })();
  }

  return refreshPromise;
}

/**
 * Core request function. Returns parsed JSON or throws ApiError.
 * Pass `T = Response` to receive the raw Response (e.g. for binary downloads).
 */
export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const { json, body: rawBody, skipAuth, headers: customHeaders, ...rest } = options;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(skipAuth ? {} : getAuthHeader()),
    ...((customHeaders as Record<string, string>) ?? {}),
  };

  let body: BodyInit | undefined;
  if (json !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(json);
  } else if (rawBody !== undefined) {
    body = rawBody;
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path), { ...rest, headers, body });
  } catch (networkErr) {
    throw new ApiError({
      status: 0,
      code: 'network_error',
      message: networkErr instanceof Error ? networkErr.message : 'Network error',
    });
  }

  if (res.status === 401 && !(options as { _retried?: boolean })._retried) {
    // Attempt a refresh-and-retry exactly once. No-op if backend doesn't support it yet.
    const newToken = await tryRefreshAccessToken();
    if (newToken) {
      return apiRequest<T>(path, {
        ...options,
        _retried: true,
      } as RequestOptions & { _retried?: boolean });
    }
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(STORAGE_KEYS.JWT_TOKEN);
      window.localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    }
    dispatchUnauthorized();
    throw await fromResponse(res);
  }

  if (res.status === 401) {
    // Retry already attempted — surface as auth error.
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(STORAGE_KEYS.JWT_TOKEN);
      window.localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    }
    dispatchUnauthorized();
    throw await fromResponse(res);
  }

  if (!res.ok) {
    throw await fromResponse(res);
  }

  // Caller wants raw Response
  if ((options as { raw?: boolean }).raw) {
    return res as unknown as T;
  }

  // 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return (await res.json()) as T;
  }
  // Non-JSON: return text as T (rare, callers should specify)
  return (await res.text()) as unknown as T;
}

// Convenience wrappers
export const apiGet = <T = unknown>(path: string, opts?: RequestOptions) =>
  apiRequest<T>(path, { ...opts, method: 'GET' });

export const apiPost = <T = unknown>(path: string, json?: unknown, opts?: RequestOptions) =>
  apiRequest<T>(path, { ...opts, method: 'POST', json });

export const apiPatch = <T = unknown>(path: string, json?: unknown, opts?: RequestOptions) =>
  apiRequest<T>(path, { ...opts, method: 'PATCH', json });

export const apiPut = <T = unknown>(path: string, json?: unknown, opts?: RequestOptions) =>
  apiRequest<T>(path, { ...opts, method: 'PUT', json });

export const apiDelete = <T = unknown>(path: string, opts?: RequestOptions) =>
  apiRequest<T>(path, { ...opts, method: 'DELETE' });

/** Download a file and trigger a browser download. UI-layer concern kept tight.
 *  Optional `opts` lets callers POST to endpoints that build files server-side
 *  (e.g. the per-area PDF inventory report). Defaults to GET.
 */
export async function apiDownload(
  path: string,
  filename: string,
  opts: RequestOptions = {}
): Promise<void> {
  const res = await apiRequest<Response>(path, { ...opts, raw: true } as RequestOptions);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
